"use client";
import React from "react";
import { Stack, Paper, Button, Toolbar, FormControl } from "@mui/material";
import {
  FormDesignerByIdDocument,
  ContentForm,
  StoreFormDocument,
} from "@quales/data-access/gql-server";
import { Form, JSONSchema7 } from "../../ui/react-jsonschema-form";
import validator from "@bpipartners/rjsf-validator-ajv";
import { SchemaForm, UiSchemaForm } from "../../forms";
import { useWindowDimensions } from "@quales/utils/hooks";
import { useTranslations } from "next-intl";
import { SubHeader } from "../../ui/layout";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { SnackBar } from "../../ui/snack-bar";
import { useRouter } from "next/navigation";
import { ErrorBoundary } from "react-error-boundary";
import FormViewerTreeComponent from "./FormViewerTreeComponent";
import lodash from "lodash-es";
import deepdash from "deepdash-es";
import {
  GetFormSchemaItem,
  GetFormSchemaPath,
  GetUIFormSchemaItem,
  GetUISchemaPath,
} from "./utils";
import { CustomTextField } from "../../ui/input";
import { NodeModel } from "@minoru/react-dnd-treeview";
import FormPropertiesSwitcher from "./FormPropertiesSwitcher";
import { GetFormStateAfterPropertiesChange } from "./utils/GetFormStateAfterPropertiesChange";
import { GetDuplicateItem } from "./utils/GetDuplicateItem";
import { GetNewItemAdded } from "./utils/GetNewItemAdded";
import { useMutation, useQuery } from "@apollo/client";
import { useTheme } from "@quales/utils/theme";

// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars, no-unused-vars
const _ = deepdash(lodash);
interface ViewFormCompositeProps {
  formID: any;
  children?: any;
  onSubmitClicked: (formData: any, submitted: boolean) => void;
}

export const ViewFormComposite = React.forwardRef(
  ({ formID, onSubmitClicked, children }: ViewFormCompositeProps, ref) => {
    const windowSize = useWindowDimensions();
    const [useLiveValidateState, setUseLiveValidateState] =
      React.useState(false);
    const router = useRouter();
    const theme = useTheme();
    const [currentlySelectedNode, setCurrentlySelectedNode] =
      React.useState<any>();
    const [formContent, setFormContent] = React.useState<ContentForm>();
    const t = useTranslations();
    const [snackBar, setSnackBar] = React.useState(false);
    const [severity, setSeverity] = React.useState("");
    const [snackBarMessage, setSnackBarMessage] = React.useState("");
    const submitFormRef = React.createRef();
    const submitSchemaRef = React.createRef();

    const {
      data: formData,
      loading: formLoading,
      refetch,
    } = useQuery(FormDesignerByIdDocument, {
      variables: {
        formID: formID || "",
      },
      fetchPolicy: "network-only",
    });

    React.useEffect(() => {
      if (!formLoading && formData && formData.FormDesignerByID) {
        setFormContent(formData.FormDesignerByID);
      } else {
        setFormContent({
          formVersion: "1",
          formID: null,
          formSchema: {},
          uiSchema: {},
          name: "",
          description: "",
          defaultData: {},
        });
      }
    }, [formData, formLoading]);

    const transformErrors = (errors: any[]) =>
      errors.map((error: any) => {
        if (useLiveValidateState === false) {
          setUseLiveValidateState(true);
        }
        if (error.name === "required") {
          error.message = t("requiredField");
        }
        return error;
      });

    const handleOnError = (val) => {
      setTimeout(() => {
        setSeverity("error");
        setSnackBar(true);
        setSnackBarMessage(t("feedback.missingValues"));
      }, 1000);
    };

    const handleClose = (__event?: React.SyntheticEvent, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      setSnackBar(false);
    };

    const handleTreeSelected = (item: any) => {
      if (item && item.id === "root") {
        setCurrentlySelectedNode({
          ...formContent.formSchema,
          name: "root",
          formSchema: { ...formContent.formSchema },
          uiSchema: { ...formContent.uiSchema },
          uiOptions: formContent.uiSchema["ui:options"],
        });
      } else {
        const formItem = GetFormSchemaItem(item.id, formContent);
        const getSchemaPath = GetFormSchemaPath(item.id, formContent);
        const getUISchemaPath = GetUISchemaPath(getSchemaPath);
        const localUISchemaCopy = lodash.get(
          formContent.uiSchema,
          getUISchemaPath
        );
        if (formItem) {
          if (localUISchemaCopy) {
            setCurrentlySelectedNode({
              ...formItem,
              name: item.id,
              formSchema: { ...formItem },
              uiSchema: { ...localUISchemaCopy },
              uiSchemaPath: getUISchemaPath,
              uiField: localUISchemaCopy["ui:field"],
              uiOptions: localUISchemaCopy["ui:options"],
              uiWidget: localUISchemaCopy["ui:widget"],
            });
          } else {
            setCurrentlySelectedNode({
              ...formItem,
              name: item.id,
              formSchema: { ...formItem },
              uiSchemaPath: getUISchemaPath,
              uiOptions: {},
            });
          }
        }
      }
    };

    const handleUiSchema = (val: string) => {
      //@ts-ignore
      if (val && val.value) {
        //@ts-ignore
        setFormContent({ ...formContent, uiSchema: JSON.parse(val.value) });

        setCurrentlySelectedNode(undefined);
      }
    };

    const handleFormSchema = (val: any) => {
      if (val && val.value) {
        setCurrentlySelectedNode(null);
        setFormContent((prev) => ({
          ...prev,
          formSchema: JSON.parse(val.value) as JSONSchema7,
        }));
      }
    };

    const handleOnUpdateMutationCompleted = function () {
      setTimeout(() => {
        setSnackBarMessage(t("feedback.updateSuccess"));
        setSnackBar(true);
        setSeverity("success");
        refetch();
      }, 1000);
    };
    const [storeForm] = useMutation(StoreFormDocument, {
      onCompleted: handleOnUpdateMutationCompleted,
    });

    const onValueChanged = (formValues) => {
      setFormContent({ ...formContent, defaultData: formValues.formData });
    };

    const handleStoreForm = () => {
      storeForm({
        variables: {
          input: {
            formSchema: formContent.formSchema,
            uiSchema: formContent.uiSchema,
            formID: formContent.formID,
            formVersion: formContent.formVersion,
            description: formContent.description,
            name: formContent.name,
          },
        },
      }).catch((error: any) => {
        console.log(error);
      });
    };

    const handleNewForm = () => {
      storeForm({
        variables: {
          input: {
            formSchema: {},
            uiSchema: {},
            formID: "0",
            formVersion: "1",
          },
        },
      })
        .then((val: any) => {
          if (val) {
            setFormContent(undefined);
            router.push(`${val.data.StoreForm.formID}`);
          }
        })
        .catch((error: any) => {
          console.log(error);
        });
    };

    const onSubmit = (val: any, e: any) => {
      if (onSubmitClicked) {
        onSubmitClicked(val.formData, true);
      }
    };

    React.useImperativeHandle(ref, () => ({
      onSubmit() {
        if (onSubmitClicked) {
          //@ts-ignore
          submitFormRef.current.click();
        }
      },
    }));

    const newItemAdded = (node: any, type: string) => {
      const addedObject = GetNewItemAdded(node, type, formContent);

      setFormContent((prevState: ContentForm) => ({
        ...prevState,
        formSchema: addedObject.formSchema,
        type: addedObject.type,
        uiSchema: addedObject.uiSchema,
      }));
    };

    const handleFormStateChange = (item) => {
      const newFormcontent = GetFormStateAfterPropertiesChange(
        item,
        formContent,
        currentlySelectedNode
      );

      setCurrentlySelectedNode({
        ...newFormcontent.selectedNode,
      });

      setFormContent((prev) => ({
        ...prev,
        formSchema: newFormcontent.formContentItem.formSchema,
        uiSchema: newFormcontent.formContentItem.uiSchema,
      }));
    };

    const handleNewUiOrder = (uiOrderArray: string[], parentID: string) => {
      if (parentID && parentID.toString() === "root") {
        const newUiSchema = lodash.cloneDeep(formContent.uiSchema);
        setFormContent((prevstate) => ({
          ...prevstate,
          uiSchema: { ...newUiSchema, "ui:order": uiOrderArray },
        }));
      } else {
        const newUiSchema = lodash.cloneDeep(formContent.uiSchema);
        const getScehmaPath = GetFormSchemaPath(parentID, formContent);
        const getUiSchemaPathParent = GetUISchemaPath(getScehmaPath);
        const parentUiSchema = GetUIFormSchemaItem(parentID, formContent);
        lodash.set(newUiSchema, getUiSchemaPathParent, {
          ...parentUiSchema,
          "ui:order": uiOrderArray,
        });

        setFormContent((prevstate) => ({
          ...prevstate,
          uiSchema: newUiSchema,
        }));
      }
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormContent((prevstate) => ({
        ...prevstate,
        name: event.target.value,
      }));
    };

    const handleDuplicateItem = (node: NodeModel) => {
      const duplicateItem = GetDuplicateItem(node, formContent);
      setFormContent((prevState: ContentForm) => ({
        ...prevState,
        formSchema: duplicateItem.formSchema,
        uiSchema: duplicateItem.uiSchema,
      }));
    };

    return (
      <Stack
        width={
          windowSize.width -
          50 -
          (theme.dimensions.totalAppDrawerWidth + theme.dimensions.margin)
        }
        spacing={2}
        id="ViewFormComposite"
      >
        <SubHeader>
          <Stack direction={"row"} spacing={2}>
            <Stack direction={"row"} spacing={2}>
              <FormControl variant="outlined" sx={{ width: 400 }}>
                <CustomTextField
                  controldisabled={false}
                  id="outlined-adornment-email"
                  label={t("labels.formName")}
                  value={(formContent && formContent.name) || ""}
                  required
                  onChange={handleNameChange}
                />
              </FormControl>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              width={"100%"}
              justifyContent="end"
            >
              <Toolbar variant="dense">
                <Button
                  sx={{ marginRight: 2 }}
                  variant="contained"
                  color="primary"
                  onClick={handleStoreForm}
                  startIcon={<SaveIcon />}
                >
                  {t("labels.saveForm")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNewForm}
                  startIcon={<AddIcon />}
                >
                  {t("labels.addForm")}
                </Button>
              </Toolbar>
            </Stack>
          </Stack>
        </SubHeader>

        <Stack
          spacing={2}
          padding={2}
          direction="row"
          overflow="scroll"
          height={
            windowSize.height
              ? windowSize.height - theme.dimensions.heightAppBar - 120
              : "80%"
          }
        >
          <Stack minWidth={400}>
            <FormViewerTreeComponent
              inputFormData={formContent}
              onContentCopyClicked={handleDuplicateItem}
              onTreeNodeClicked={handleTreeSelected}
              onNewItem={newItemAdded}
              onNewUiOrderObject={handleNewUiOrder}
            ></FormViewerTreeComponent>
          </Stack>

          <Stack width={500}>
            {currentlySelectedNode &&
              currentlySelectedNode.formSchema &&
              Object.keys(currentlySelectedNode.formSchema).length > 0 && (
                <FormPropertiesSwitcher
                  selectedNode={currentlySelectedNode}
                  type={currentlySelectedNode.type}
                  handleStateChange={handleFormStateChange}
                />
              )}
          </Stack>

          <Stack minWidth={600}>
            {!formLoading &&
              formContent &&
              formContent.formSchema &&
              formContent.uiSchema && (
                <Stack
                  height={windowSize.height ? windowSize.height - 150 : "80%"}
                  spacing={2}
                  padding={1}
                  minWidth={600}
                  maxWidth={800}
                  overflow="auto"
                >
                  <Paper>
                    <Stack padding={1}>
                      <ErrorBoundary
                        fallbackRender={({ error, resetErrorBoundary }) => (
                          <div role="alert">
                            <div>Oh no</div>
                            <pre>{error.message}</pre>
                            <button
                              onClick={() => {
                                // this next line is why the fallbackRender is useful
                                // resetComponentState();
                                // though you could accomplish this with a combination
                                // of the FallbackCallback and onReset props as well.
                                resetErrorBoundary();
                              }}
                            >
                              Try again
                            </button>
                          </div>
                        )}
                      >
                        <Form
                          schema={
                            formContent &&
                            formContent.formSchema !== undefined &&
                            formContent.formSchema
                          }
                          uiSchema={
                            formContent &&
                            formContent.uiSchema !== undefined &&
                            formContent.uiSchema
                          }
                          onSubmit={onSubmit}
                          readonly={false}
                          onError={handleOnError}
                          noHtml5Validate={true}
                          onChange={onValueChanged}
                          liveValidate={useLiveValidateState}
                          formData={
                            (formContent && formContent.defaultData) || {}
                          }
                          ismobile={false}
                          transformErrors={transformErrors}
                          validator={validator}
                        >
                          {children && children}
                          <button
                            //@ts-ignore
                            ref={submitFormRef}
                            type="submit"
                            style={{ display: "none" }}
                          />
                        </Form>
                      </ErrorBoundary>
                    </Stack>
                  </Paper>
                </Stack>
              )}
          </Stack>
          {!formLoading && (
            <Stack spacing={2} minWidth={600}>
              <Paper>
                <SchemaForm
                  data={formContent && formContent}
                  onValueChange={handleFormSchema}
                  ref={submitSchemaRef}
                />
              </Paper>
              <Stack>
                <Paper>
                  <UiSchemaForm
                    data={formContent && formContent}
                    onValueChange={handleUiSchema}
                  />
                </Paper>
              </Stack>
            </Stack>
          )}
        </Stack>

        {snackBar && (
          <Stack spacing={2} sx={{ width: "100%" }}>
            <SnackBar
              openState={snackBar}
              //@ts-ignore
              severity={severity}
              message={snackBarMessage}
              //@ts-ignore
              onClose={handleClose}
            ></SnackBar>
          </Stack>
        )}
      </Stack>
    );
  }
);
ViewFormComposite.displayName = "ViewFormComposite";
export default ViewFormComposite;
